/**
 * App-level upload session manager.
 *
 * Owns in-flight tus uploads keyed by a stable client id, exposes
 * pause/resume/cancel/retry methods, and aggregates progress/state for
 * the UploadDock UI. Uploads live above the route tree, so navigating
 * between drives, workspaces, or settings does not cancel an upload.
 *
 * Server-side stages (scanning/writing) come in via WebSocket
 * upload:progress events keyed by the same id.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef, type ReactNode } from 'react'
import * as tus from 'tus-js-client'
import { pinnedBatch, isPinnedBatchIdentityLost, type PinnedBatch, type RequestIdentity } from '@papan/api-client'
import { buildTusUpload, MalwareDetectedError, clearStaleTusSessions, type ConflictStrategy, type TusUploadResult } from '@papan/files/client'
import { useRealtime } from '@papan/realtime/client'
import { admitUploads } from './upload-queue'
import { useToastOptional } from '@papan/core-ui/ui'
import { useTranslation } from '@papan/i18n'

export type UploadStatus =
  | 'queued'
  | 'uploading'
  | 'paused'
  | 'scanning'
  | 'writing'
  | 'completed'
  | 'failed'
  | 'cancelled'

/**
 * Bulk-upload concurrency cap. Dropping a large folder used to call `start()`
 * for every file in a tight loop, each firing its tus `upload.start()`
 * immediately → thousands of simultaneous tus Upload instances (each with an
 * 8 MB chunk buffer + XHR + state) → client OOM. We now admit at most K uploads
 * into the active tus phase and QUEUE the rest, refilling as each reaches a
 * terminal state. Peak memory is bounded to K × chunkSize regardless of folder
 * size. Tunable via VITE_UPLOAD_CONCURRENCY; default 6 (the classic HTTP/1.1
 * per-host connection limit — higher wastes slots on queued connections).
 */
const MAX_CONCURRENT_UPLOADS = (() => {
  const raw = Number((import.meta as { env?: Record<string, string | undefined> }).env?.VITE_UPLOAD_CONCURRENCY)
  return Number.isInteger(raw) && raw > 0 ? raw : 6
})()

export interface UploadEntry {
  id: string
  fileName: string
  fileSize: number
  parentId: string | null
  conflictStrategy?: ConflictStrategy
  path?: string

  status: UploadStatus
  bytesUploaded: number
  startedAt: number
  completedAt?: number
  error?: string
  malware?: { threat: string; scanner: string }

  fileId?: string
  versionId?: string
  resultPath?: string
  skipped?: boolean
  /** #1090 — the upload replaced a large app database: that Replace runs in the background (its record). */
  replacingRecordId?: string
}

export interface UploadStartOptions {
  fileName: string
  parentId: string | null
  conflictStrategy?: ConflictStrategy
  path?: string
  description?: string
  /** Fires once when this upload reaches a terminal state (completed | failed | cancelled). */
  onTerminal?: (entry: UploadEntry) => void
  /** #1316 review round 2: the batch the upload belongs to (`pinnedBatch()`) — taken when it is STARTED (queued) if not
   *  given. When the file is SENT its identity follows the batch's rule (`batch.opts()`): a named identity goes as the
   *  person who dropped it, never the next session; one that names nothing goes as the tab while its session lasts and
   *  is FAILED once that session ended (sent with the tenant alone, the server would pick the NEW identity). */
  batch?: PinnedBatch
}

export interface UploadManager {
  uploads: UploadEntry[]
  inProgressCount: number
  start(file: File | Blob, options: UploadStartOptions): { id: string; promise: Promise<UploadEntry> }
  pause(id: string): void
  resume(id: string): void
  cancel(id: string): void
  retry(id: string, file: File | Blob): { promise: Promise<UploadEntry> }
  clear(id: string): void
  clearCompleted(): void
  /** Dismiss all failed entries. They don't auto-clear (only completed do), so on
   *  a systematic large-drop failure they pile up with no other way to remove them
   *  short of a reload. Dismiss-only: retry needs the original File, which the
   *  manager doesn't retain. */
  clearFailed(): void
}

const Ctx = createContext<UploadManager | null>(null)

type State = { uploads: Record<string, UploadEntry> }
type Action =
  | { type: 'add'; entry: UploadEntry }
  | { type: 'patch'; id: string; patch: Partial<UploadEntry> }
  | { type: 'remove'; id: string }
  | { type: 'clearCompleted' }
  | { type: 'clearFailed' }

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'add':
      return { uploads: { ...state.uploads, [action.entry.id]: action.entry } }
    case 'patch': {
      const existing = state.uploads[action.id]
      if (!existing) return state
      return { uploads: { ...state.uploads, [action.id]: { ...existing, ...action.patch } } }
    }
    case 'remove': {
      if (!state.uploads[action.id]) return state
      const { [action.id]: _, ...rest } = state.uploads
      return { uploads: rest }
    }
    case 'clearCompleted': {
      const next: Record<string, UploadEntry> = {}
      for (const [id, entry] of Object.entries(state.uploads)) {
        if (entry.status !== 'completed' && entry.status !== 'cancelled') {
          next[id] = entry
        }
      }
      return { uploads: next }
    }
    case 'clearFailed': {
      const next: Record<string, UploadEntry> = {}
      for (const [id, entry] of Object.entries(state.uploads)) {
        if (entry.status !== 'failed') next[id] = entry
      }
      return { uploads: next }
    }
  }
}

function genId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID()
  return Math.random().toString(36).slice(2) + Date.now().toString(36)
}

function isTerminal(status: UploadStatus): boolean {
  return status === 'completed' || status === 'failed' || status === 'cancelled'
}

export function UploadManagerProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, { uploads: {} })
  const tusRef = useRef<Map<string, tus.Upload>>(new Map())
  const terminalCallbacksRef = useRef<Map<string, (entry: UploadEntry) => void>>(new Map())
  const promiseHandlersRef = useRef<Map<string, { resolve: (e: UploadEntry) => void; reject: (err: Error) => void }>>(new Map())
  const stateRef = useRef(state)
  stateRef.current = state
  // #1090 — a large app database is replaced in the background: the dock folds a completed row into "N done" and then
  // closes, so the person is told by a toast (the outcome follows as a notice).
  const toast = useToastOptional()
  const { t } = useTranslation('files')
  const queuedToastRef = useRef((_name: string) => {})
  queuedToastRef.current = (name: string) => toast?.info(t('driveView.toast.dbReplaceQueued', { name }))
  // #1316 review round 2: the failed row's text for a file whose pinned identity was lost (read when the file is sent)
  const identityLostTextRef = useRef('')
  identityLostTextRef.current = t('driveView.toast.uploadIdentityLost')

  // Concurrency gate (see MAX_CONCURRENT_UPLOADS). `activeIdsRef` = ids currently
  // in the tus active phase (built + started, not yet terminal). `pendingRef` =
  // FIFO of admitted-but-not-yet-started uploads. `pumpRef` breaks the callback
  // cycle (settleTerminal → pump → startUpload → settleTerminal) via a ref.
  const activeIdsRef = useRef<Set<string>>(new Set())
  const pendingRef = useRef<Array<{ id: string; file: File | Blob; opts: UploadStartOptions }>>([])
  const pumpRef = useRef<() => void>(() => {})

  const realtime = useRealtime()

  const settleTerminal = useCallback((id: string, finalEntry: UploadEntry) => {
    const onTerminal = terminalCallbacksRef.current.get(id)
    onTerminal?.(finalEntry)
    terminalCallbacksRef.current.delete(id)

    const handlers = promiseHandlersRef.current.get(id)
    if (handlers) {
      if (finalEntry.status === 'completed') handlers.resolve(finalEntry)
      else handlers.reject(new Error(finalEntry.error || 'Upload failed'))
      promiseHandlersRef.current.delete(id)
    }

    tusRef.current.delete(id)
    // Free the concurrency slot and admit the next queued upload. Idempotent:
    // a double-settle (tus onSuccess + WS 'completed') deletes an absent id and
    // pump() is capped by activeIdsRef.size, so it never over-admits.
    activeIdsRef.current.delete(id)
    pumpRef.current()
  }, [])

  // Subscribe to server-side upload:progress events (scanning, writing,
  // completed, failed). Keyed by the same id we put in tus metadata.
  useEffect(() => {
    if (!realtime?.onUploadEvent) return
    return realtime.onUploadEvent((event) => {
      const id = event.uploadId
      const existing = stateRef.current.uploads[id]
      if (!existing) return

      const patch: Partial<UploadEntry> = {}
      if (event.stage === 'scanning' || event.stage === 'writing') {
        patch.status = event.stage
      } else if (event.stage === 'completed') {
        patch.status = 'completed'
        patch.completedAt = Date.now()
      } else if (event.stage === 'failed') {
        patch.status = 'failed'
        patch.error = event.error
        patch.completedAt = Date.now()
      }
      if (Object.keys(patch).length > 0) {
        dispatch({ type: 'patch', id, patch })
      }

      if (event.stage === 'completed' || event.stage === 'failed') {
        const next = { ...existing, ...patch } as UploadEntry
        settleTerminal(id, next)
      }
    })
  }, [realtime, settleTerminal])

  // Drop stale tus session URLs from localStorage on app load.
  // Files can't persist across reload, so old sessions are unresumable —
  // clearing prevents quota bloat and noise.
  useEffect(() => {
    clearStaleTusSessions()
      .then((n) => { if (n > 0) console.info(`[uploads] cleared ${n} stale tus sessions`) })
      .catch(() => { /* swallow */ })
  }, [])

  // Tab close / refresh: best-effort DELETE on every in-flight tus session
  // so the server can drop the temp file immediately instead of waiting for
  // the cron sweep. fetch keepalive lets the request survive page unload.
  useEffect(() => {
    const onBeforeUnload = () => {
      for (const upload of tusRef.current.values()) {
        const url = upload.url
        if (!url) continue
        try {
          fetch(url, {
            method: 'DELETE',
            credentials: 'include',
            headers: { 'Tus-Resumable': '1.0.0' },
            keepalive: true,
          })
        } catch { /* best effort */ }
      }
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [])

  // Auto-clear completed entries after 10s so the dock self-tidies.
  useEffect(() => {
    const completedIds = Object.values(state.uploads)
      .filter((u) => u.status === 'completed' && u.completedAt && Date.now() - u.completedAt > 10_000)
      .map((u) => u.id)
    if (completedIds.length === 0) {
      const nextSweepIn = Math.min(
        ...Object.values(state.uploads)
          .filter((u) => u.status === 'completed' && u.completedAt)
          .map((u) => 10_000 - (Date.now() - u.completedAt!)),
      )
      if (Number.isFinite(nextSweepIn) && nextSweepIn > 0) {
        const t = setTimeout(() => {
          // re-trigger by dispatching a no-op patch on any entry; cheaper to just
          // clear directly from a fresh stateRef snapshot.
          const stale = Object.values(stateRef.current.uploads)
            .filter((u) => u.status === 'completed' && u.completedAt && Date.now() - u.completedAt > 10_000)
          for (const u of stale) dispatch({ type: 'remove', id: u.id })
        }, nextSweepIn + 100)
        return () => clearTimeout(t)
      }
      return
    }
    for (const id of completedIds) dispatch({ type: 'remove', id })
  }, [state.uploads])

  const startUpload = useCallback((id: string, file: File | Blob, opts: UploadStartOptions) => {
    const existing = stateRef.current.uploads[id]
    const entry: UploadEntry = existing ?? {
      id,
      fileName: opts.fileName,
      fileSize: file.size,
      parentId: opts.parentId,
      conflictStrategy: opts.conflictStrategy,
      path: opts.path,
      status: 'uploading',
      bytesUploaded: 0,
      startedAt: Date.now(),
    }
    if (!existing) {
      dispatch({ type: 'add', entry })
    } else {
      dispatch({ type: 'patch', id, patch: { status: 'uploading', error: undefined, malware: undefined } })
    }

    if (opts.onTerminal) terminalCallbacksRef.current.set(id, opts.onTerminal)

    // #1316 review round 2: the batch's rule decides the identity NOW, when the file is sent (it may have waited in the
    // queue past a switch) — an identity that can no longer be named fails this file instead of going as the next one
    let identity: RequestIdentity | undefined
    try {
      identity = opts.batch?.opts().identity
    } catch (err) {
      if (!isPinnedBatchIdentityLost(err)) throw err
      const patch: Partial<UploadEntry> = { status: 'failed', error: identityLostTextRef.current, completedAt: Date.now() }
      dispatch({ type: 'patch', id, patch })
      // settled after this admission pass (it frees the slot and pumps the queue again)
      queueMicrotask(() => settleTerminal(id, { ...entry, ...patch } as UploadEntry))
      return
    }

    const upload = buildTusUpload(opts.parentId, opts.fileName, file, {
      uploadId: id,
      ...(identity ? { identity } : {}),
      conflictStrategy: opts.conflictStrategy,
      path: opts.path,
      description: opts.description,
      onProgress: (loaded) => {
        // Only patch wire progress while client is uploading. Server-side
        // stages (scanning/writing) take over via WebSocket events.
        const cur = stateRef.current.uploads[id]
        if (cur && cur.status === 'uploading') {
          dispatch({ type: 'patch', id, patch: { bytesUploaded: loaded } })
        }
      },
      onSuccess: (result: TusUploadResult) => {
        // Wire upload finished and the server's finalize hook already
        // ran (its response is what triggered onSuccess). Treat this as
        // authoritative: settle the entry as completed regardless of
        // whether the WebSocket 'completed' event has been observed
        // yet — the upload IS done from the server's perspective.
        const cur = stateRef.current.uploads[id]
        const patch: Partial<UploadEntry> = {
          status: 'completed',
          fileId: result.fileId,
          versionId: result.versionId,
          resultPath: result.path,
          skipped: result.skipped,
          ...(result.queued ? { replacingRecordId: result.queued.recordId } : {}),
          bytesUploaded: file.size,
          completedAt: Date.now(),
        }
        dispatch({ type: 'patch', id, patch })
        if (result.queued) queuedToastRef.current(cur?.fileName ?? '')
        if (cur) settleTerminal(id, { ...cur, ...patch } as UploadEntry)
      },
      onError: (err) => {
        const cur = stateRef.current.uploads[id]
        const patch: Partial<UploadEntry> = {
          status: 'failed',
          error: err.message,
          completedAt: Date.now(),
        }
        if (err instanceof MalwareDetectedError) {
          patch.malware = { threat: err.threat, scanner: err.scanner }
        }
        dispatch({ type: 'patch', id, patch })
        if (cur) settleTerminal(id, { ...cur, ...patch } as UploadEntry)
      },
    })

    tusRef.current.set(id, upload)

    upload
      .findPreviousUploads()
      .then((prev) => {
        if (prev.length > 0) upload.resumeFromPreviousUpload(prev[0])
        upload.start()
      })
      .catch(() => upload.start())
  }, [settleTerminal])

  // Admit as many queued uploads into the active tus phase as the cap allows.
  // Called after every enqueue and every terminal settle. Admission invariant
  // lives in the pure `admitUploads` (unit-tested).
  const pump = useCallback(() => {
    admitUploads(activeIdsRef.current, pendingRef.current, MAX_CONCURRENT_UPLOADS, (item) => {
      startUpload(item.id, item.file, item.opts)
    })
  }, [startUpload])
  pumpRef.current = pump

  const enqueue = useCallback((id: string, file: File | Blob, opts: UploadStartOptions) => {
    pendingRef.current.push({ id, file, opts })
    pump()
  }, [pump])

  const start = useCallback((file: File | Blob, opts: UploadStartOptions) => {
    const id = genId()
    const promise = new Promise<UploadEntry>((resolve, reject) => {
      promiseHandlersRef.current.set(id, { resolve, reject })
    })
    // Register the entry as `queued` up front so the dock shows the full batch
    // immediately; `pump` promotes it to `uploading` when a slot frees. This is
    // the gate that bounds a thousand-file folder drop to K concurrent tus uploads.
    dispatch({
      type: 'add',
      entry: {
        id,
        fileName: opts.fileName,
        fileSize: file.size,
        parentId: opts.parentId,
        conflictStrategy: opts.conflictStrategy,
        path: opts.path,
        status: 'queued',
        bytesUploaded: 0,
        startedAt: Date.now(),
      },
    })
    if (opts.onTerminal) terminalCallbacksRef.current.set(id, opts.onTerminal)
    enqueue(id, file, { ...opts, batch: opts.batch ?? pinnedBatch() })
    return { id, promise }
  }, [enqueue])

  const pause = useCallback((id: string) => {
    const upload = tusRef.current.get(id)
    if (!upload) return
    void upload.abort()
    dispatch({ type: 'patch', id, patch: { status: 'paused' } })
  }, [])

  const resume = useCallback((id: string) => {
    const upload = tusRef.current.get(id)
    if (!upload) return
    upload.start()
    dispatch({ type: 'patch', id, patch: { status: 'uploading' } })
  }, [])

  const cancel = useCallback((id: string) => {
    // Drop it from the pending queue if it hasn't been admitted yet (else pump
    // would later start a cancelled entry).
    pendingRef.current = pendingRef.current.filter((q) => q.id !== id)
    const upload = tusRef.current.get(id)
    if (upload) {
      void upload.abort(true) // true = also DELETE the server session
      tusRef.current.delete(id)
    }
    const cur = stateRef.current.uploads[id]
    dispatch({ type: 'patch', id, patch: { status: 'cancelled', completedAt: Date.now() } })
    if (cur) {
      settleTerminal(id, { ...cur, status: 'cancelled' as const, completedAt: Date.now() })
    }
  }, [settleTerminal])

  const retry = useCallback((id: string, file: File | Blob) => {
    const cur = stateRef.current.uploads[id]
    if (!cur) {
      throw new Error(`Cannot retry unknown upload ${id}`)
    }
    const promise = new Promise<UploadEntry>((resolve, reject) => {
      promiseHandlersRef.current.set(id, { resolve, reject })
    })
    dispatch({ type: 'patch', id, patch: { status: 'queued', error: undefined, malware: undefined, bytesUploaded: 0, completedAt: undefined } })
    enqueue(id, file, {
      fileName: cur.fileName,
      parentId: cur.parentId,
      conflictStrategy: cur.conflictStrategy,
      path: cur.path,
    })
    return { promise }
  }, [enqueue])

  const clear = useCallback((id: string) => {
    const cur = stateRef.current.uploads[id]
    if (cur && !isTerminal(cur.status)) return // refuse to clear in-flight; cancel first
    dispatch({ type: 'remove', id })
  }, [])

  const clearCompleted = useCallback(() => {
    dispatch({ type: 'clearCompleted' })
  }, [])

  const clearFailed = useCallback(() => {
    dispatch({ type: 'clearFailed' })
  }, [])

  const value = useMemo<UploadManager>(() => {
    const ordered = Object.values(state.uploads).sort((a, b) => {
      const aTerminal = isTerminal(a.status) ? 1 : 0
      const bTerminal = isTerminal(b.status) ? 1 : 0
      if (aTerminal !== bTerminal) return aTerminal - bTerminal
      return b.startedAt - a.startedAt
    })
    const inProgress = ordered.filter((u) => !isTerminal(u.status)).length
    return {
      uploads: ordered,
      inProgressCount: inProgress,
      start,
      pause,
      resume,
      cancel,
      retry,
      clear,
      clearCompleted,
      clearFailed,
    }
  }, [state.uploads, start, pause, resume, cancel, retry, clear, clearCompleted, clearFailed])

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useUploadManager(): UploadManager {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useUploadManager must be used inside UploadManagerProvider')
  return ctx
}
